/**
 * Apollo Client configuration for GraphQL API
 */

import {
  ApolloClient,
  CombinedGraphQLErrors,
  InMemoryCache,
  HttpLink,
  from,
  split,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { store } from '../store';

// Get GraphQL URL from environment or use default
const GRAPHQL_ENDPOINT = import.meta.env.VITE_GRAPHQL_URL || 'http://localhost:3001/graphql';
const GRAPHQL_WS_ENDPOINT =
  import.meta.env.VITE_GRAPHQL_WS_URL || GRAPHQL_ENDPOINT.replace(/^http/, 'ws');

/**
 * Error handling link - logs GraphQL and network errors
 */
const errorLink = onError(({ error, operation }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, locations, path, extensions }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`,
        extensions
      );
    });
  } else {
    console.error(`[Network error]: ${String(error)}`, {
      operation: operation.operationName,
    });
  }
});

/**
 * Auth link — injects Bearer token from Redux store into every GraphQL request
 */
const authLink = setContext((_, { headers }) => {
  const token = store.getState().auth.token;
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };
});

/**
 * HTTP link for GraphQL requests
 */
const httpLink = new HttpLink({
  uri: GRAPHQL_ENDPOINT,
  credentials: 'same-origin', // Only send cookies for same-origin requests
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Edge type for GraphQL connection pagination
 */
interface ConnectionEdge {
  cursor: string;
  node: unknown;
}

/** Minimal shape of a Relay-style connection needed by the merge policy. */
interface PaginatedConnection {
  edges?: ConnectionEdge[];
}

/**
 * Merge policy for Relay-style connections that the frontend fully paginates
 * client-side (see `useAllPages` in graphql/queries/useAllPages.ts). Paired with
 * `keyArgs: ['filter']` so every page of the same filtered query shares one
 * cache entry: the first page (no `after`) replaces, and each subsequent
 * `fetchMore` page appends its edges (deduped by cursor). Without this,
 * `fetchMore` would replace the edges and the list would stay capped at one
 * page — the artificial 50-row limit this policy removes.
 */
function mergePaginatedConnection(
  existing: PaginatedConnection | undefined,
  incoming: PaginatedConnection,
  { args }: { args: { after?: string } | null }
): PaginatedConnection {
  if (!existing || !args?.after) {
    return incoming;
  }
  const existingCursors = new Set((existing.edges || []).map((edge) => edge.cursor));
  const newEdges = (incoming.edges || []).filter((edge) => !existingCursors.has(edge.cursor));
  return {
    ...incoming,
    edges: [...(existing.edges || []), ...newEdges],
  };
}

/**
 * In-memory cache configuration with type policies
 */
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        services: {
          // Merge incoming data with existing data for pagination
          keyArgs: ['filter'],
          merge(existing, incoming, { args }) {
            // Always replace on first page or when no cursor
            if (!existing || !args?.after) {
              return incoming;
            }

            // Subsequent pages - merge edges, filtering duplicates by cursor
            const existingCursors = new Set(
              (existing.edges || []).map((edge: ConnectionEdge) => edge.cursor)
            );
            const newEdges = (incoming.edges || []).filter(
              (edge: ConnectionEdge) => !existingCursors.has(edge.cursor)
            );

            return {
              ...incoming,
              edges: [...(existing.edges || []), ...newEdges],
            };
          },
        },
        technologies: {
          // Merge incoming data with existing data for pagination (issue #765,
          // mirrors the `services` policy above).
          keyArgs: ['filter'],
          merge(existing, incoming, { args }) {
            // Always replace on first page or when no cursor
            if (!existing || !args?.after) {
              return incoming;
            }

            // Subsequent pages - merge edges, filtering duplicates by cursor
            const existingCursors = new Set(
              (existing.edges || []).map((edge: ConnectionEdge) => edge.cursor)
            );
            const newEdges = (incoming.edges || []).filter(
              (edge: ConnectionEdge) => !existingCursors.has(edge.cursor)
            );

            return {
              ...incoming,
              edges: [...(existing.edges || []), ...newEdges],
            };
          },
        },
        servicesByTeam: {
          keyArgs: ['teamId'],
          merge(existing, incoming, { args }) {
            if (!existing || !args?.after) {
              return incoming;
            }

            return {
              ...incoming,
              edges: [...(existing.edges || []), ...incoming.edges],
            };
          },
        },
        // Client-fully-paginated connections (see `useAllPages`). Each is keyed
        // by `filter` and merges `fetchMore` pages so the list is not capped at
        // one page (the old 50-row artificial limit).
        capabilities: { keyArgs: ['filter'], merge: mergePaginatedConnection },
        businessUnits: { keyArgs: ['filter'], merge: mergePaginatedConnection },
        businessProcesses: { keyArgs: ['filter'], merge: mergePaginatedConnection },
        physicalProcesses: { keyArgs: ['filter'], merge: mergePaginatedConnection },
        businessEnvironmentFactors: { keyArgs: ['filter'], merge: mergePaginatedConnection },
        teams: { keyArgs: ['filter'], merge: mergePaginatedConnection },
        technologyDomains: { keyArgs: ['filter'], merge: mergePaginatedConnection },
        maturityAssessments: { keyArgs: ['filter'], merge: mergePaginatedConnection },
      },
    },
    Service: {
      // Use entityId as the cache key for Service entities
      keyFields: ['entityId'],
    },
    Team: {
      // Use entityId as the cache key for Team entities
      keyFields: ['entityId'],
    },
  },
});

/**
 * Create and configure Apollo Client instance
 */
export const createApolloClient = (): ApolloClient => {
  const wsLink = new GraphQLWsLink(
    createClient({
      url: GRAPHQL_WS_ENDPOINT,
      lazy: true, // Only connect when a subscription is active (Lambda-compatible; avoids eager WS on page load)
      connectionParams: () => {
        const token = store.getState().auth.token;
        return token ? { Authorization: `Bearer ${token}` } : {};
      },
    })
  );

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    from([authLink, errorLink, httpLink])
  );

  return new ApolloClient({
    link: splitLink,
    cache,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
      },
      query: {
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
      },
      mutate: {
        errorPolicy: 'all',
      },
    },
    devtools: {
      enabled: import.meta.env.DEV,
    },
  });
};

/**
 * Singleton Apollo Client instance
 */
export const apolloClient = createApolloClient();

export default apolloClient;
